import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { generatePaginationOptions, updateUrlQueryParam } from "lib/helper";
import { selectLoading } from "modules/notification";
import {
  getDealerRequestList,
  resetDealerRequestListing,
  updateRequestListTab,
} from "modules/stockflow/redux";
import moment from "moment";
import DealerRequestListPanelComponent from "./dealerRequestListPanel.component";
import {AccessRights} from "lib/constants/accessRights";
import {canAccess} from "../../../../profile/redux/selector/profile.selector";

function DealerRequestListPanelContainer({ type }) {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const history = useHistory();
  const id = match.params.dealerId;
  const lang = useSelector((state) => state.constant.languages);
  const query = new URLSearchParams(window.location.search);

  const isRequestFetching = useSelector((state) =>
    selectLoading(state, getDealerRequestList.typePrefix)
  );
  const hasStockflowOrderViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.TrackcoPortal.ORDER_READ));
  const hasStockflowDealerViewAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.TrackcoPortal.DEALER_READ));

  const totalFiltered = useSelector(
    (state) => state.stockflowDealer.requestList.list[`${type}`].totalFiltered
  );
  const totalRecords = useSelector(
    (state) => state.stockflowDealer.requestList.list[`${type}`].totalRecords
  );
  const requestList = useSelector(
    (state) => state.stockflowDealer.requestList.list[`${type}`].list
  );

  const isError = useSelector(
    (state) => state.stockflowDealer.requestList.list.isError
  );
  const isSearchResult = requestList.length < totalRecords;
  const [paginationOptions, setPaginationOptions] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFilterUpdated, setIsFilterUpdated] = useState(false);
  const [tableFilter, setTableFilter] = useState({
    page: 0,
    rowsPerPage: 25,
    order: "desc",
    orderBy: "created_at",
    search: "",
    fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
    requestTo: [],
    respondDate: null,
  });

  const tableFilterInitialRef = useRef(true);

  useEffect(() => {
    return () => {
      dispatch(resetDealerRequestListing());
      dispatch(updateRequestListTab("new"));
    };
  }, [dispatch]);

  useEffect(() => {
    const isFilter = query.has("is_filter");
    let filter = sessionStorage.getItem(`dealer_request_filter_${id}`);

    let temp = { ...tableFilter };
    if (isFilter && filter) {
      temp = JSON.parse(filter);
      setIsFilterUpdated(true);
      setIsFilterOpen(true);
    }

    if (isFilter) {
      updateUrlQueryParam(null, ["is_filter"]);
    }
    sessionStorage.removeItem(`dealer_request_filter_${id}`);

    setTableFilter(temp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tableFilterInitialRef.current) {
      dispatch(updateRequestListTab(type));
      dispatch(resetDealerRequestListing());
      getData();
    }
    tableFilterInitialRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilter, type]);

  useEffect(() => {
    setPaginationOptions(generatePaginationOptions(totalFiltered));
  }, [totalFiltered]);

  const getData = () => {
    dispatch(
      getDealerRequestList({
        id,
        length: tableFilter.rowsPerPage,
        start: tableFilter.rowsPerPage * tableFilter.page,
        order: tableFilter.order,
        order_by: tableFilter.orderBy,
        type,
        filter: {
          search: tableFilter.search,
          from_date: tableFilter.fromDate,
          to_date: tableFilter.toDate,
          request_to: tableFilter.requestTo,
          respond_date: tableFilter.respondDate,
        },
      })
    );
  };

  const handleChangePage = (event, newPage) => {
    let newData = {
      ...tableFilter,
      page: newPage,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const handleChangeRowsPerPage = (event) => {
    let newData = {
      ...tableFilter,
      page: 0,
      rowsPerPage: event.target.value,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const handleSearch = (filter) => {
    const { search, dateRange, respondDate, requestTo } = filter;

    let newData = {
      ...tableFilter,
      page: 0,
      search: search,
      fromDate: dateRange?.from
        ? moment(dateRange.from).format("YYYY-MM-DD")
        : "",
      toDate: dateRange?.to ? moment(dateRange.to).format("YYYY-MM-DD") : "",
      respondDate: respondDate,
      requestTo: requestTo,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const clearSearch = () => {
    let newData = {
      ...tableFilter,
      page: 0,
      search: "",
      fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      requestTo: [],
      respondDate: null,
    };
    setIsFilterUpdated(false);
    setTableFilter(newData);
  };

  const resetFilter = () => {
    let newData = {
      ...tableFilter,
      page: 0,
      respondDate: "",
      requestTo: [],
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const changeOrder = (orderBy, order) => {
    let newData = {
      ...tableFilter,
      order,
      orderBy,
    };
    setIsFilterUpdated(true);
    setTableFilter(newData);
  };

  const handleViewDetail = (url) => {
    if (isFilterUpdated) {
      updateUrlQueryParam({ is_filter: true });
      sessionStorage.setItem(
        `dealer_request_filter_${id}`,
        JSON.stringify(tableFilter)
      );
    }
    history.push(url);
  };

  return (
    <DealerRequestListPanelComponent
      hasStockflowOrderViewAccessRight={hasStockflowOrderViewAccessRight}
      hasStockflowDealerViewAccessRight={hasStockflowDealerViewAccessRight}
      id={id}
      type={type}
      requests={requestList}
      isFetching={isRequestFetching}
      isError={isError}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      page={tableFilter.page}
      rowsPerPage={tableFilter.rowsPerPage}
      totalFiltered={totalFiltered}
      handleSearch={handleSearch}
      clearSearch={clearSearch}
      handleReload={getData}
      isSearchResult={isSearchResult}
      paginationOptions={paginationOptions}
      isFilterOpen={isFilterOpen}
      setIsFilterOpen={setIsFilterOpen}
      tableFilter={tableFilter}
      changeOrder={changeOrder}
      lang={lang}
      resetFilter={resetFilter}
      handleViewDetail={handleViewDetail}
    />
  );
}

export default DealerRequestListPanelContainer;
